import React, { useEffect } from "react";
import {
  retrieveLaunchParams,
  retrieveRawInitData,
} from "@telegram-apps/sdk-react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/store/authStore";

function hasRealTelegramInitData(initData: string | null | undefined): boolean {
  if (!initData) return false;

  const hash = new URLSearchParams(initData).get("hash");
  return Boolean(hash);
}

export const AuthProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { sessionToken, setAuth, setInitData, setSessionToken } = useAuth();
  const { search } = useLocation();
  const telegramLaunch = retrieveLaunchParams();
  const rawInitData = retrieveRawInitData();
  const tgId = telegramLaunch.tgWebAppData?.user?.id;

  const adminParam = new URLSearchParams(search).get("admin");

  const initData: string | null =
    adminParam ||
    (hasRealTelegramInitData(rawInitData) ? rawInitData ?? null : null);
  useEffect(() => {
    const params = new URLSearchParams(search);

    const tgIdFromParams = params.get("tg_id");
    if (tgIdFromParams) {
      setAuth(tgIdFromParams, "telegram");
      return;
    }

    if (tgId) {
      setAuth(String(tgId), "telegram");
    }
  }, [tgId, search, setAuth]);

  useEffect(() => {
    setInitData(initData);
  }, [initData, setInitData]);

  useEffect(() => {
    const manifestLink = document.querySelector<HTMLLinkElement>(
      'link[rel="manifest"]'
    );
    if (!manifestLink || !sessionToken) return;

    let objectUrl: string | null = null;
    let isCancelled = false;

    const updateManifestStartUrl = async () => {
      try {
        const response = await fetch("/manifest.webmanifest", {
          cache: "no-store",
        });
        const manifest = await response.json();
        const startUrl = new URL(window.location.href);

        startUrl.searchParams.set("auth_session", sessionToken);

        const nextManifest = {
          ...manifest,
          id: manifest.id || "/",
          start_url: `${startUrl.pathname}${startUrl.search}${startUrl.hash}`,
        };

        const blob = new Blob([JSON.stringify(nextManifest)], {
          type: "application/manifest+json",
        });
        objectUrl = URL.createObjectURL(blob);

        if (!isCancelled) {
          manifestLink.href = objectUrl;
        }
      } catch (e) {
        console.error("Failed to update web app manifest:", e);
      }
    };

    updateManifestStartUrl();

    return () => {
      isCancelled = true;
      manifestLink.href = "/manifest.webmanifest";

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [sessionToken]);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const authSession = params.get("auth_session");
    if (!authSession) return;

    setSessionToken(authSession);

    const telegramId = authSession.split(":")[1];
    if (telegramId) {
      setAuth(telegramId, "telegram_session");
    }

    params.delete("auth_session");
    const query = params.toString();
    const nextUrl = `${window.location.pathname}${query ? `?${query}` : ""}${
      window.location.hash
    }`;
    window.history.replaceState(null, "", nextUrl);
  }, [search, setAuth, setSessionToken]);

  return <>{children}</>;
};
